import { supabase } from '@/lib/supabase';
import type { ChatSource, ChatSourceOption } from '@shared/types/chat-sources';
import type { DashboardChatMessage } from '@shared/types/dashboard-chat';

/** D1: sources cited with « @ » in the side chat. */

/** Options shown in the « @ » menu: the size of the menu, not a business cap. */
const PICKER_OPTIONS = 12;

export const searchChatSources = async (query: string): Promise<ChatSourceOption[]> => {
  const { data, error } = await supabase.rpc('search_chat_sources_d4', { p_query: query, p_limit: PICKER_OPTIONS });
  if (error) throw new Error('Les sources ne sont pas accessibles pour le moment.');
  const count = (detail: string | null, noun: string) => {
    const value = Number(detail ?? 0);
    return `${value} ${noun}${value > 1 ? 's' : ''}`;
  };
  return ((data ?? []) as Array<{ kind: string; source_id: string; parent_id: string | null; label: string; detail: string | null }>).flatMap((row): ChatSourceOption[] => {
    if (row.kind === 'knowledge_folder') {
      return [{ ref: { kind: 'knowledge_folder' as const, folderId: row.source_id }, label: row.label, detail: count(row.detail, 'document') }];
    }
    if (row.kind === 'app_list') {
      return [{ ref: { kind: 'app_list' as const, appId: row.source_id, labelIds: [] }, label: row.label, detail: count(row.detail, 'objet') }];
    }
    if (row.kind === 'app_label' && row.parent_id) {
      return [{ ref: { kind: 'app_list' as const, appId: row.parent_id, labelIds: [row.source_id] }, label: row.label, detail: count(row.detail, 'objet') }];
    }
    if (row.kind === 'knowledge_tag') {
      return [{ ref: { kind: 'knowledge_tag' as const, tagId: row.source_id }, label: row.label, detail: count(row.detail, 'document') }];
    }
    if (row.kind === 'account') {
      const open = Number(row.detail ?? 0);
      return [{ ref: { kind: 'account' as const, subjectId: row.source_id }, label: row.label, detail: open ? `${open} tâche${open > 1 ? 's' : ''}` : null }];
    }
    if (row.kind === 'app_folder') {
      return [{ ref: { kind: 'app_folder' as const, folderId: row.source_id }, label: row.label, detail: count(row.detail, 'objet') }];
    }
    if (row.kind === 'knowledge_document') {
      return [{ ref: { kind: 'knowledge_document' as const, documentId: row.source_id }, label: row.label, detail: row.detail }];
    }
    return [];
  });
};

/** The chips of a conversation: the sources of its latest message (none once they were removed). */
export const latestCitedSources = (messages: DashboardChatMessage[]): ChatSource[] => {
  const latest = [...messages].reverse().find((message) => message.role === 'user');
  return latest?.context?.sources ?? [];
};

/** Where a set cited by the chat opens: its app's list, filtered as the chat read it (AppRecordsView). */
export const appSetHref = (citation: { appKey: string; query: string; status: string | null; labelId: string | null }): string => {
  const params = new URLSearchParams();
  if (citation.query) params.set('q', citation.query);
  if (citation.status) params.set('statut', citation.status);
  if (citation.labelId) params.set('etiquette', citation.labelId);
  const search = params.toString();
  return `/apps/${citation.appKey}${search ? `?${search}` : ''}`;
};

/** Where a cited document opens: its drawer in Profil › Documents. */
export const documentHref = (documentId: string): string => `/profil?tab=documents&doc=${documentId}`;
